import { useEffect, useRef } from "react"
import { getToken } from "../lib/auth"
import type { VideoStatus } from "../types/video"

export type VideoStatusEvent = {
  videoId: string
  status: VideoStatus
  errorMessage?: string | null
  duration?: number
}

type Options = {
  onEvent: (event: VideoStatusEvent) => void
  /** Red de seguridad si el WebSocket nunca logra conectar (ej. proxy corporativo que lo bloquea) — debe hacer un patch puntual, nunca un refetch completo de tabla. */
  onFallbackPoll?: () => void
}

const MAX_BACKOFF_MS = 30_000
// Solo se activa el polling de emergencia si el WS lleva este tiempo sin
// lograr abrir ni una vez — no corre en paralelo a un WS sano. Ver
// openspec/changes/video-status-realtime-push.
const FALLBACK_AFTER_MS = 60_000

function buildWsUrl(): string | null {
  const token = getToken()
  if (!token) return null
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api"
  const url = new URL(base, window.location.origin)
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:"
  url.pathname = `${url.pathname.replace(/\/$/, "")}/academy/admin/videos/ws`
  url.searchParams.set("token", token)
  return url.toString()
}

/**
 * Reemplaza el polling de 5s de VideosManager.tsx/VideoFormPage.tsx: abre un
 * único WebSocket contra el VideoStatusHubDO de platforms/api y reenvía cada
 * evento a `onEvent` para que el consumidor actualice solo la fila afectada
 * (nunca un refetch de tabla completa). Reconecta con backoff exponencial;
 * si nunca logra abrir, activa `onFallbackPoll` cada 60s como red de
 * seguridad.
 */
export function useVideoStatusSocket({ onEvent, onFallbackPoll }: Options): void {
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent
  const onFallbackPollRef = useRef(onFallbackPoll)
  onFallbackPollRef.current = onFallbackPoll

  useEffect(() => {
    let socket: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let fallbackTimer: ReturnType<typeof setInterval> | null = null
    let attempt = 0
    let everConnected = false
    let stopped = false
    const firstAttemptAt = Date.now()

    function stopFallback() {
      if (fallbackTimer) {
        clearInterval(fallbackTimer)
        fallbackTimer = null
      }
    }

    function maybeStartFallback() {
      if (fallbackTimer || everConnected) return
      if (Date.now() - firstAttemptAt < FALLBACK_AFTER_MS) return
      fallbackTimer = setInterval(() => onFallbackPollRef.current?.(), 60_000)
    }

    function scheduleReconnect() {
      if (stopped) return
      maybeStartFallback()
      const delay = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS)
      attempt += 1
      reconnectTimer = setTimeout(connect, delay)
    }

    function connect() {
      if (stopped) return
      const url = buildWsUrl()
      if (!url) {
        // Sin sesión todavía (login en curso) — reintenta más tarde, sin
        // contar como intento fallido de conexión real.
        reconnectTimer = setTimeout(connect, 2000)
        return
      }

      socket = new WebSocket(url)

      socket.onopen = () => {
        attempt = 0
        everConnected = true
        stopFallback()
      }

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string) as VideoStatusEvent
          onEventRef.current(data)
        } catch {
          // mensaje malformado — se ignora
        }
      }

      socket.onclose = () => {
        socket = null
        scheduleReconnect()
      }

      socket.onerror = () => {
        socket?.close()
      }
    }

    connect()

    return () => {
      stopped = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      stopFallback()
      socket?.close()
    }
  }, [])
}
